import React, { useEffect } from 'react';
import { Building2, ClipboardCheck, Megaphone, LineChart, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import SectionHeading from '@/components/site/SectionHeading';
import Reveal from '@/components/site/Reveal';
import Process from '@/components/home/Process';
import { Button } from '@/components/ui/button';

const BLOCKS = [
  {
    icon: Building2,
    title: 'Commercial Leasing',
    summary: 'Office, retail, coworking, and mixed-use leasing handled end-to-end — from market positioning to lease registration.',
    items: [
      { name: 'Office Leasing', desc: 'Grade-A, boutique, and HQ-style office spaces across Hyderabad micro-markets.' },
      { name: 'Retail Leasing', desc: 'High-street, mall, and anchor showroom leasing for premium brands.' },
      { name: 'Coworking Leasing', desc: 'Operator expansion and corporate managed-floor leasing.' },
      { name: 'Tenant Representation', desc: 'Buyer-side mandates for GCCs, corporates, and growth-stage occupiers.' },
      { name: 'Landlord Representation', desc: 'Disciplined leasing mandates that protect rentals and asset positioning.' },
      { name: 'Lease Negotiation', desc: 'Commercial and legal negotiation across LOI, MoU, and registered lease deeds.' },
    ],
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=2000&q=80'
  },
  {
    icon: ClipboardCheck,
    title: 'Property Management',
    summary: 'Disciplined day-to-day operations that protect asset value and tenant experience.',
    items: [
      { name: 'Facility Coordination', desc: 'Housekeeping, security, MEP, and lifecycle maintenance management.' },
      { name: 'Vendor Management', desc: 'Scorecards, SLA tracking, and structured vendor rationalization.' },
      { name: 'Rent Collection Oversight', desc: 'Disciplined collection rhythm with structured escalation paths.' },
      { name: 'Tenant Relationship Management', desc: 'NPS-led tenant engagement to drive renewals and reduce churn.' },
      { name: 'Asset Performance Monitoring', desc: 'Quarterly asset reviews, occupancy mapping, and yield analysis.' },
      { name: 'Occupancy Optimization', desc: 'Vacancy refilling playbooks aligned to landlord financial outcomes.' },
    ],
    image: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=2000&q=80'
  },
  {
    icon: Megaphone,
    title: 'Marketing & Tenant Acquisition',
    summary: 'Targeted marketing programs that shorten vacancy windows and attract better-quality tenants.',
    items: [
      { name: 'Property Branding', desc: 'Branded e-brochures, professional shoots, 3D walkthroughs, and signage.' },
      { name: 'Digital Marketing', desc: 'Performance media + warm broker network distribution.' },
      { name: 'Lead Generation', desc: 'Targeted outreach to shortlisted occupiers and corporate decision-makers.' },
      { name: 'Property Showings', desc: 'Structured site-visit coordination with score-card based feedback loops.' },
      { name: 'Market Positioning', desc: 'Rental benchmarking, asset narrative, and target-tenant profiling.' },
    ],
    image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=2000&q=80'
  },
  {
    icon: LineChart,
    title: 'Real Estate Advisory',
    summary: 'Data-led advisory for portfolio owners, investors, and corporate occupiers.',
    items: [
      { name: 'Market Analysis', desc: 'Micro-market dossiers across Hyderabad commercial submarkets.' },
      { name: 'Rental Benchmarking', desc: 'Indicative rent ranges by location, type, and quality grade.' },
      { name: 'Investment Advisory', desc: 'Acquisition due-diligence and yield modelling support.' },
      { name: 'Portfolio Strategy', desc: 'Portfolio rationalization, asset clustering, and CapEx prioritization.' },
    ],
    image: 'https://images.unsplash.com/photo-1554469384-e58fac16e23a?auto=format&fit=crop&w=2000&q=80'
  },
];

export default function ServicesPage() {
  useEffect(() => { document.title = 'Services | Cespedis Commercial Real Estate Hyderabad'; }, []);
  return (
    <div data-testid="services-page">
      <section className="relative isolate overflow-hidden bg-[#144726] pt-32 pb-20 md:pt-40 md:pb-24">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#144726] via-[#1A5A33] to-[#144726]" />
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C6A35B]">Services</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight max-w-3xl">A complete commercial real estate practice, under one roof.</h1>
          <p className="mt-5 max-w-2xl text-base md:text-lg text-white/75">Leasing, property management, marketing, and advisory — four interconnected practices, delivered with institutional rigour.</p>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 space-y-20 md:space-y-28">
          {BLOCKS.map((b, i) => (
            <Reveal key={b.title}>
              <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
                <div className={`lg:col-span-6 ${i % 2 ? 'lg:order-2' : ''}`}>
                  <div className="overflow-hidden rounded-2xl">
                    <img src={b.image} alt={b.title} className="h-[360px] w-full object-cover md:h-[480px]" />
                  </div>
                </div>
                <div className="lg:col-span-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#C6A35B]/15 text-[#144726]"><b.icon className="h-6 w-6" /></div>
                  <h2 className="mt-5 font-display text-3xl md:text-4xl font-semibold text-[#144726] tracking-tight">{b.title}</h2>
                  <p className="mt-3 text-slate-600 leading-relaxed">{b.summary}</p>
                  <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {b.items.map((it) => (
                      <div key={it.name} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <p className="font-semibold text-[#144726] text-sm">{it.name}</p>
                        <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{it.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Process />

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 sm:px-6 lg:px-8 md:flex-row md:items-center">
          <SectionHeading kicker="Ready to discuss?" title="Let's talk about your mandate." />
          <Link to="/contact"><Button className="h-12 rounded-lg bg-[#144726] px-6 text-white hover:bg-[#0E3520]">Schedule a Consultation <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
        </div>
      </section>
    </div>
  );
}
